import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';

import ProductCard, { ProductCardSkeleton } from './product-card';

import { ppHeadingOverridesClose } from '../../shared/utils';
import Logo from '../logo';

const OutfitsPopup = ({
  outfit,
  products,
  isProductsLoading,
  isPopupOpen,
  setIsPopupOpen,
  userSession,
  buttonInfo = {
    text: 'Shop Brand',
    url: '/'
  }
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [basketCount, setBasketCount] = useState(0);

  const closeTimerRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const count = Number(
      document.querySelector('.cdnBasket')?.textContent || 0
    );
    setBasketCount(Number.isNaN(count) ? 0 : count);
  }, []);

  const closePopupCleanUp = useCallback(() => {
    setIsPopupOpen(false);
    setIsClosing(false);
    window.scrollLock();
    ppHeadingOverridesClose();
  }, [setIsPopupOpen, setIsClosing]);

  const closePopup = useCallback(() => {
    setIsClosing(true);
    window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = window.setTimeout(() => {
      closePopupCleanUp();
    }, 400);
  }, [setIsClosing, closePopupCleanUp]);

  useEffect(() => {
    if (!isPopupOpen) return undefined;

    closeButtonRef.current?.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePopup();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(closeTimerRef.current);
    };
  }, [isPopupOpen, closePopup]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="outfits-popup-title"
      className={`fixed inset-0 z-[20] p-0 ${isPopupOpen ? 'block' : 'hidden'}`}
    >
      <div
        id="outfits-overlay"
        className={isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}
        onClick={closePopup}
      />
      <div
        id="outfits-content"
        className={
          isClosing
            ? isMobile
              ? 'fade-out-down'
              : 'fade-out-right'
            : isMobile
            ? 'fade-in-up'
            : 'fade-in-left'
        }
      >
        <span className="sr-only" id="outfits-popup-title">
          Seven Outfits
        </span>
        <PopupHeader
          closePopup={closePopup}
          userSession={userSession}
          basketCount={basketCount}
          closeButtonRef={closeButtonRef}
        />
        <div className="bg-[rgba(4,_21,_35,_0.50)] h-full overflow-y-auto scrollbar-hide lg:overflow-y-hidden lg:h-screen pt-24 pb-16 flex flex-col lg:grid grid-cols-[2fr_3fr] lg:gap-16">
          <div className="p-6 flex flex-col gap-10 lg:p-16 lg:pr-0">
            <h3 className="flex flex-col gap-2 text-white !text-lg">
              <b>{outfit?.large_text}</b>
              {/* <span className="block w-8 rotate-90 h-px bg-white" /> */}
              <span className="!text-md">{outfit?.small_text}</span>
            </h3>
            <div className="flex flex-row lg:flex-col">
              <div className="relative flex-1">
                <img
                  src={outfit?.image}
                  alt={outfit?.alt_text}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex-1 bg-black text-white flex flex-col justify-between gap-6">
                <p className="!text-base tracking-normal lg:!leading-7">
                  {outfit?.description}
                </p>
                <a
                  href={`${buttonInfo.url}${userSession}`}
                  className="flex flex-row items-center justify-between bg-white/20 py-5 px-6 hover:bg-black transition-colors duration-300"
                >
                  <p className="!text-base">
                    <b>{buttonInfo.text}</b>
                  </p>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12.2768 7.46433L8.3393 11.4018C8.21601 11.5251 8.0488 11.5944 7.87445 11.5944C7.7001 11.5944 7.53289 11.5251 7.40961 11.4018C7.28633 11.2785 7.21707 11.1113 7.21707 10.937C7.21707 10.7626 7.28633 10.5954 7.40961 10.4721L10.2266 7.65628H2.1875C2.01345 7.65628 1.84653 7.58714 1.72346 7.46407C1.60039 7.341 1.53125 7.17408 1.53125 7.00003C1.53125 6.82599 1.60039 6.65907 1.72346 6.536C1.84653 6.41292 2.01345 6.34379 2.1875 6.34378H10.2266L7.4107 3.52628C7.28742 3.403 7.21816 3.23579 7.21816 3.06144C7.21816 2.88709 7.28742 2.71988 7.4107 2.5966C7.53399 2.47331 7.7012 2.40405 7.87555 2.40405C8.0499 2.40405 8.21711 2.47331 8.34039 2.5966L12.2779 6.5341C12.3391 6.59515 12.3876 6.66768 12.4207 6.74754C12.4538 6.8274 12.4707 6.913 12.4706 6.99944C12.4705 7.08588 12.4534 7.17144 12.4201 7.25122C12.3868 7.331 12.3381 7.40343 12.2768 7.46433Z"
                      fill="white"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="p-3 flex flex-col gap-6 lg:p-16 lg:pl-0 lg:h-screen lg:overflow-y-auto scrollbar-hide lg:max-h-[calc(100vh-60px)]">
            {products
              ? products.map((product, index) => (
                  <ProductCard
                    key={product?.productid || index}
                    product={product}
                    userSession={userSession}
                    setBasketCount={setBasketCount}
                    imageCarousel
                  />
                ))
              : isProductsLoading &&
                Array.from({ length: 2 }, (_, index) => (
                  <ProductCardSkeleton
                    key={index}
                    product={null}
                    userSession={userSession}
                    setBasketCount={setBasketCount}
                  />
                ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const PopupHeader = ({
  closePopup,
  userSession,
  basketCount,
  closeButtonRef
}) => {
  return (
    <div className="bg-black/80 backdrop-blur-xl flex flex-row items-center justify-between p-3 pl-6 absolute inset-x-0 top-0 z-10 lg:pl-16">
      <Logo.Joint className="w-[25rem]" />
      <div className="flex flex-row items-center gap-3">
        <a
          href={'/basket/' + userSession}
          className="bg-white/10 p-4 relative hover:bg-white/40 transition-colors duration-300"
        >
          {basketCount > 0 && (
            <div className="absolute top-3 right-3 w-[6px] h-[6px] bg-[#24E57B] rounded-full" />
          )}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20.25 5.62492H16.8591C16.7601 4.40418 16.2053 3.26541 15.305 2.4351C14.4046 1.6048 13.2247 1.1438 12 1.1438C10.7753 1.1438 9.59538 1.6048 8.69504 2.4351C7.79471 3.26541 7.23989 4.40418 7.14094 5.62492H3.75C3.25272 5.62492 2.77581 5.82247 2.42417 6.1741C2.07254 6.52573 1.875 7.00264 1.875 7.49992V18.7499C1.875 19.2472 2.07254 19.7241 2.42417 20.0757C2.77581 20.4274 3.25272 20.6249 3.75 20.6249H20.25C20.7473 20.6249 21.2242 20.4274 21.5758 20.0757C21.9275 19.7241 22.125 19.2472 22.125 18.7499V7.49992C22.125 7.00264 21.9275 6.52573 21.5758 6.1741C21.2242 5.82247 20.7473 5.62492 20.25 5.62492ZM12 3.37492C12.6312 3.37499 13.2412 3.60247 13.7183 4.0157C14.1954 4.42892 14.5076 5.00022 14.5978 5.62492H9.40219C9.49236 5.00022 9.80459 4.42892 10.2817 4.0157C10.7588 3.60247 11.3688 3.37499 12 3.37492ZM19.875 18.3749H4.125V7.87492H7.125V8.99992C7.125 9.29829 7.24353 9.58444 7.4545 9.79542C7.66548 10.0064 7.95163 10.1249 8.25 10.1249C8.54837 10.1249 8.83452 10.0064 9.0455 9.79542C9.25647 9.58444 9.375 9.29829 9.375 8.99992V7.87492H14.625V8.99992C14.625 9.29829 14.7435 9.58444 14.9545 9.79542C15.1655 10.0064 15.4516 10.1249 15.75 10.1249C16.0484 10.1249 16.3345 10.0064 16.5455 9.79542C16.7565 9.58444 16.875 9.29829 16.875 8.99992V7.87492H19.875V18.3749Z"
              fill="white"
            />
          </svg>
        </a>
        <button
          ref={closeButtonRef}
          type="button"
          className="bg-white/10 p-4 cursor-pointer hover:bg-white/40 transition-colors duration-300"
          onClick={closePopup}
          aria-label="Close outfits popup"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19.5459 17.954C19.7572 18.1653 19.876 18.452 19.876 18.7509C19.876 19.0497 19.7572 19.3364 19.5459 19.5477C19.3346 19.7591 19.0479 19.8778 18.749 19.8778C18.4501 19.8778 18.1635 19.7591 17.9521 19.5477L12 13.5937L6.0459 19.5459C5.83455 19.7572 5.54791 19.8759 5.24902 19.8759C4.95014 19.8759 4.66349 19.7572 4.45215 19.5459C4.2408 19.3345 4.12207 19.0479 4.12207 18.749C4.12207 18.4501 4.2408 18.1635 4.45215 17.9521L10.4062 11.9999L4.45402 6.04586C4.24268 5.83451 4.12395 5.54787 4.12395 5.24898C4.12395 4.9501 4.24268 4.66345 4.45402 4.45211C4.66537 4.24076 4.95201 4.12203 5.2509 4.12203C5.54978 4.12203 5.83643 4.24076 6.04777 4.45211L12 10.4062L17.954 4.45117C18.1654 4.23983 18.452 4.12109 18.7509 4.12109C19.0498 4.12109 19.3364 4.23983 19.5478 4.45117C19.7591 4.66251 19.8778 4.94916 19.8778 5.24804C19.8778 5.54693 19.7591 5.83358 19.5478 6.04492L13.5937 11.9999L19.5459 17.954Z"
              fill="white"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default OutfitsPopup;
